import { useEffect } from 'react'

import { track } from '.'

/**
 * The DOM events that count as a first interaction, each mapped to the `interaction_type` reported.
 *
 * `mousemove` is included because the point of the event is to separate a launch someone looked at
 * from one they only loaded, and a mouse crossing the window is the earliest evidence of the former.
 * `keydown` reports as `type` rather than a key name: what matters is that input happened.
 */
const INTERACTIONS = {
  click: 'click',
  keydown: 'type',
  mousemove: 'mouse_movement',
  scroll: 'scroll',
} as const

/**
 * Reports the session's first user interaction, then stops listening.
 *
 * A suite-level event rather than an app's: the first interaction can happen on the login screen or
 * the hospital picker, before any app mounts, so it belongs to the shell's **Surface**.
 */
export const FirstUserInteractionTracker = () => {
  useEffect(() => {
    const controller = new AbortController()

    const handleInteraction = (event: Event) => {
      const interactionType =
        INTERACTIONS[event.type as keyof typeof INTERACTIONS]
      if (!interactionType) {
        return
      }
      controller.abort()
      track.firstUserInteractionRecorded({ interaction_type: interactionType })
    }

    for (const domEvent of Object.keys(INTERACTIONS)) {
      window.addEventListener(domEvent, handleInteraction, {
        capture: true,
        passive: true,
        signal: controller.signal,
      })
    }

    return () => controller.abort()
  }, [])

  return null
}
